import React, {
    ChangeEvent,
    ChangeEventHandler,
    MouseEventHandler,
    useMemo,
} from 'react';
import Box from '@mui/material/Box';
import { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import { smsFormat } from '@core/interfaces/sms-format';
import { IServerState } from '@core/interfaces/ISettings';
import { BindedHandler } from '@core/interfaces/binded-handler';
import InputComponent from '@ui/components/Input/Input.component';
import SelectComponent from '@ui/components/Select/Select.component';
import useDictionary from '@ui/hooks/useDictionary';
import SwitchComponent from './Switch.component';
import ServerItemComponent from './Server-item.component';
import { lang } from '@lang/interfaces/lang';

interface IProps {
    lang: lang;
    smsState: boolean;
    smsFormat: number;
    smsFormats: smsFormat[];
    phoneNumSim1: string;
    phoneNumSim2: string;
    serverState: IServerState<boolean>;
    serverProtocol: IServerState<string>;
    serverAddress: IServerState<string>;
    serverPort: IServerState<number | null>;
    serverPassword: IServerState<string>;
    mainSim: number;
    advancedMode: boolean;
    onSmsStateChange: () => void;
    onSmsFormatChange: SelectProps['onChange'];
    onInputClick: () => void;
    onPhoneNumSim1Change: ChangeEventHandler<HTMLInputElement>;
    onPhoneNumSim2Change: ChangeEventHandler<HTMLInputElement>;
    onServerStateChange: MouseEventHandler<HTMLElement>;
    onServerProtocolChange: BindedHandler<SelectChangeEvent<string>>;
    onServerAddressChange: BindedHandler<ChangeEvent<HTMLInputElement>>;
    onServerPortChange: BindedHandler<ChangeEvent<HTMLInputElement>>;
    onServerPasswordChange: BindedHandler<ChangeEvent<HTMLInputElement>>;
    onMainSimChange: SelectProps['onChange'];
}

const Component: React.FC<IProps> = ({
    lang,
    smsState,
    smsFormat,
    smsFormats,
    phoneNumSim1,
    phoneNumSim2,
    serverState,
    serverProtocol,
    serverAddress,
    serverPort,
    serverPassword,
    mainSim,
    advancedMode,
    onSmsStateChange,
    onSmsFormatChange,
    onInputClick,
    onPhoneNumSim1Change,
    onPhoneNumSim2Change,
    onServerStateChange,
    onServerProtocolChange,
    onServerAddressChange,
    onServerPortChange,
    onServerPasswordChange,
    onMainSimChange,
}: IProps) => {
    const getWord = useDictionary();
    const formatList = useMemo(
        () => smsFormats.map((label, value) => ({ label, value })),
        [smsFormats],
    );
    const simList = useMemo(
        () =>
            new Array(2).fill(0).map((v, i) => {
                const value = i + 1;
                const label = `SIM${value}`;
                return { label, value };
            }),
        [],
    );
    const serverItems = Object.keys(serverState).map((name) => (
        <ServerItemComponent
            key={name}
            lang={lang}
            name={name}
            serverState={serverState[name as keyof typeof serverState]!}
            serverProtocol={
                serverProtocol[name as keyof typeof serverProtocol]!
            }
            serverAddress={serverAddress[name as keyof typeof serverAddress]!}
            serverPort={serverPort[name as keyof typeof serverPort]!}
            serverPassword={
                serverPassword[name as keyof typeof serverPassword]!
            }
            onServerStateChange={onServerStateChange}
            onServerProtocolChange={onServerProtocolChange}
            onServerAddressChange={onServerAddressChange}
            onServerPortChange={onServerPortChange}
            onServerPasswordChange={onServerPasswordChange}
            onInputClick={onInputClick}
        />
    ));
    const smsFormatTooltip = new Array(2)
        .fill(0)
        .map((v, i) => `${i + 1}. ${getWord(`smsFormatTooltip_${i}`)}.`);

    return (
        <FormControl className="device-settings__form" component="form">
            <SelectComponent
                label={getWord('mainSim')}
                name="main-sim"
                description="mainSimTooltip"
                lang={lang}
                value={mainSim}
                values={simList}
                onChange={onMainSimChange}
                onClick={onInputClick}
                onFocus={onInputClick}
            />
            <SwitchComponent
                alarmState={smsState}
                name={'smsState'}
                tooltip={getWord('smsStateTooltip')}
                onAlarmStateChange={onSmsStateChange}
            />
            <SelectComponent
                label="smsFormat"
                name="sms-format"
                lang={lang}
                description={smsFormatTooltip}
                disabled={!smsState}
                value={smsFormat}
                values={formatList}
                onChange={onSmsFormatChange}
                onClick={onInputClick}
                onFocus={onInputClick}
            />
            <InputComponent
                label="phoneNumSim1"
                name="phone-number-sim1"
                type="text"
                description="phoneNumSim1Tooltip"
                lang={lang}
                value={phoneNumSim1}
                disabled={!smsState}
                onChange={onPhoneNumSim1Change}
                onClick={onInputClick}
                onFocus={onInputClick}
            />
            <InputComponent
                label="phoneNumSim2"
                name="phone-number-sim2"
                type="text"
                description="phoneNumSim2Tooltip"
                lang={lang}
                value={phoneNumSim2}
                disabled={!smsState}
                onChange={onPhoneNumSim2Change}
                onClick={onInputClick}
                onFocus={onInputClick}
            />
            {advancedMode && (
                <Box className="device-settings__server-list" component="ul">
                    {serverItems}
                </Box>
            )}
        </FormControl>
    );
};

const DataTransmissionSettingsComponent = React.memo(Component);

export default DataTransmissionSettingsComponent;
